import type {
    ICreateGateway,
    IGatewayCreateResponse,
    IGatewayCredentialIssueResponse,
    IGatewayCredentialResponse,
    IGatewayResponse,
    IUpdateGateway,
} from '@escloud/types';

import type { ApiClient } from './api-client';

/**
 * Tenant-admin gateway management endpoints.
 *
 * Note on tokens: `createGateway` and `issueCredential` return the
 * plaintext token ONCE — the caller must surface it immediately (e.g.
 * via the token-shown-once modal) and discard it from React state
 * afterwards. No subsequent endpoint will return it.
 */
export class GatewaysService {
    constructor(private api: ApiClient) {}

    listGateways(tenantId: string): Promise<{ items: IGatewayResponse[] }> {
        return this.api.get<{ items: IGatewayResponse[] }>(`/tenants/${tenantId}/gateways`);
    }

    createGateway(tenantId: string, data: ICreateGateway): Promise<IGatewayCreateResponse> {
        return this.api.post<IGatewayCreateResponse>(`/tenants/${tenantId}/gateways`, data);
    }

    updateGateway(tenantId: string, gatewayId: string, data: IUpdateGateway): Promise<IGatewayResponse> {
        return this.api.patch<IGatewayResponse>(`/tenants/${tenantId}/gateways/${gatewayId}`, data);
    }

    deleteGateway(tenantId: string, gatewayId: string): Promise<void> {
        return this.api.delete<void>(`/tenants/${tenantId}/gateways/${gatewayId}`);
    }

    listCredentials(tenantId: string, gatewayId: string): Promise<{ items: IGatewayCredentialResponse[] }> {
        return this.api.get<{ items: IGatewayCredentialResponse[] }>(
            `/tenants/${tenantId}/gateways/${gatewayId}/credentials`
        );
    }

    issueCredential(tenantId: string, gatewayId: string): Promise<IGatewayCredentialIssueResponse> {
        return this.api.post<IGatewayCredentialIssueResponse>(
            `/tenants/${tenantId}/gateways/${gatewayId}/credentials`,
            {}
        );
    }

    revokeCredential(tenantId: string, gatewayId: string, credentialId: string): Promise<void> {
        return this.api.delete<void>(
            `/tenants/${tenantId}/gateways/${gatewayId}/credentials/${credentialId}`
        );
    }
}
